import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { 
  Grid2, 
  Container, 
  Typography, 
  Paper, 
  Box, 
  CircularProgress,
  Alert
} from "@mui/material";

import { db } from "config/firebase";
import { doc, onSnapshot } from "firebase/firestore";

import Map from "app/Components/Map";
import WaitlistAccordion from "./Sections/WaitlistAccordion";
import PlayersInGameAccordion from "./Sections/PlayersInGameAccordion";

const DAYS_OF_WEEK = {
  0: "Sunday",
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday",
} as const;

const GAME_TYPES = {
  NLH: "No Limit Hold-Em",
  PLO: "Pot Limit Omaha",
} as const;

interface PlayerData {
  displayName: string;
  timeStamp: any;
  [key: string]: any;
}

interface Game {
  gameName: string;
  type: 'Cash' | 'Tournament';
  stakes?: string;
  game: keyof typeof GAME_TYPES | string;
  minbuyin?: string;
  maxbuyin?: string;
  buyin?: string;
  fee?: string;
  startDay: keyof typeof DAYS_OF_WEEK;
  startTime: string;
  description?: string;
  address?: string;
  city: string;
  zipcode: string;
  waitlist: Record<string, boolean>;
  playerlist: Record<string, PlayerData>;
  currentPlayers: Record<string, boolean>;
}

const GameDetails: React.FC = () => {
  const [game, setGame] = useState<Game | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const params = useParams<{ gameId: string }>();

  useEffect(() => {
    if (!params.gameId) {
      setError('Game ID is required');
      setLoading(false);
      return;
    }

    const gameDoc = doc(db, "games", params.gameId);
    const unsubscribe = onSnapshot(
      gameDoc, 
      (doc) => {
        if (doc.exists()) {
          setGame(doc.data() as Game);
          setError(null);
        } else {
          setError('Game not found');
          setGame(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching game:', err);
        setError('Failed to load game data');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [params.gameId]);

  const renderCashInfo = () => (
    <Box display="flex" flexDirection="column" alignItems="center">
      <Typography variant="body1" gutterBottom textAlign="center">
        {game && `${game.stakes} ${game.game}`}
      </Typography>
      {game?.minbuyin && (
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Min Buy-in: {game.minbuyin}
        </Typography>
      )}
      {game?.maxbuyin && (
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Max Buy-in: {game.maxbuyin}
        </Typography>
      )}
    </Box>
  );

  const renderTournamentInfo = () => (
    <Box display="flex" flexDirection="column" alignItems="center">
      <Typography variant="body1" gutterBottom textAlign="center">
        Game: {game && (GAME_TYPES[game.game as keyof typeof GAME_TYPES] || game.game)}
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center">
        Buy-In: {game?.buyin}
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center">
        Fee: {game?.fee}
      </Typography>
    </Box>
  );

  const renderTimeInfo = () => (
    <Box mb={2} display="flex" flexDirection="column" alignItems="center">
      <Typography variant="h6" gutterBottom textAlign="center">
        Date & Time
      </Typography>
      <Typography variant="body1" textAlign="center">
        {game && DAYS_OF_WEEK[game.startDay]} @ {game?.startTime}
      </Typography>
    </Box>
  );
  const renderDescription = () =>
    game?.description ? (
      <Box mb={2} display="flex" flexDirection="column" alignItems="center">
        <Typography variant="h6" gutterBottom textAlign="center">
          Description
        </Typography>
        <Typography 
          variant="body2" 
          component="div"
          sx={{ textAlign: 'center', maxWidth: '100%' }}
          dangerouslySetInnerHTML={{
            __html: game.description,
          }}
        />
      </Box>
    ) : null;

  const renderMapComp = () => (
    <Box 
      sx={{ 
        height: 'fit-content',
        position: { lg: 'sticky' },
        top: { lg: 2 },
        width: '100%',
        maxWidth: { xs: '100%', lg: 600 }, // Increased max width
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        // Ensure map container is responsive and centered
        '& .map-container': {
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }
      }}
    >
      <Box className="map-container">
        <Map address={game?.zipcode || ''} />
      </Box>
      <Typography 
        variant="body2" 
        color="text.secondary" 
        mt={2}
        textAlign="center"
        sx={{ 
          fontSize: { xs: '0.875rem', sm: '0.875rem', md: '1rem' }
        }}
      >
        {game?.address && `${game.address}, `}{game?.city} {game?.zipcode}
      </Typography>
    </Box>
  );

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !game) {
    return (
      <Container>
        <Box mt={4}>
          <Alert severity="error">
            {error || 'Game not found'}
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, sm: 3, md: 4, lg: 8 } }}>
      <Box display="flex" flexDirection="column" alignItems="center">
        <Typography variant="h4" component="h1" gutterBottom align="center">
          {game.gameName}
        </Typography>
        
        <Grid2 container spacing={8} sx={{ width: '100%', justifyContent: 'center', maxWidth: '1800px' }}>
          {/* Game Details Section - First on mobile, right on desktop */}
          <Grid2 
            size={{ xs: 12, lg: 6 }}
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              pl: { lg: 4 },
              order: { xs: 1, lg: 2 }
            }}
          >
            <Paper elevation={2} sx={{ p: 3, mb: 2, width: '100%', textAlign: 'center' }}>
              <Typography variant="h5" gutterBottom color="primary">
                {game.type} Game
              </Typography>
              <Box sx={{ textAlign: 'center' }}>
                {game.type === "Cash" ? renderCashInfo() : renderTournamentInfo()}
              </Box>
              <Box mt={2} sx={{ textAlign: 'center' }}>
                {renderTimeInfo()}
                {renderDescription()}
              </Box>
            </Paper>
            
            {/* Player sections - only show on large screens */}
            <Grid2 
              container 
              spacing={2} 
              sx={{ 
                width: '100%', 
                justifyContent: 'center',
                display: { xs: 'none', lg: 'flex' }
              }}
            >
              <Grid2 size={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box sx={{ width: '100%' }}>
                  <WaitlistAccordion
                    waitlist={game.waitlist}
                    playerlist={game.playerlist}
                  />
                </Box>
              </Grid2>
              
              <Grid2 size={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box sx={{ width: '100%' }}>
                  <PlayersInGameAccordion
                    currentPlayers={game.currentPlayers}
                    playerlist={game.playerlist}
                  />
                </Box>
              </Grid2>
            </Grid2>
          </Grid2>
          
          {/* Map Section - Second on mobile, left on desktop */}
          <Grid2 
            size={{ xs: 12, lg: 6 }}
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              pr: { lg: 4 },
              order: { xs: 2, lg: 1 }
            }}
          >
            {renderMapComp()}
          </Grid2>
        </Grid2>
        
        {/* Player sections - show at bottom on small screens only */}
        <Grid2 
          container 
          spacing={2} 
          sx={{ 
            width: '100%', 
            justifyContent: 'center', 
            maxWidth: '1800px',
            mt: 4,
            display: { xs: 'flex', lg: 'none' }
          }}
        >
          <Grid2 size={{ xs: 12, md: 6 }} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ width: '100%' }}>
              <WaitlistAccordion
                waitlist={game.waitlist}
                playerlist={game.playerlist}
              />
            </Box>
          </Grid2>
          
          <Grid2 size={{ xs: 12, md: 6 }} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ width: '100%' }}>
              <PlayersInGameAccordion
                currentPlayers={game.currentPlayers}
                playerlist={game.playerlist}
              />
            </Box>
          </Grid2>
        </Grid2>
      </Box>
    </Container>
  );
};

export default GameDetails;
